/**
 * Active-filter counting and per-column filter-state derivation for the overview tables (#1607). Pure
 * functions over plain filter objects — no React, no network calls — so this module counts toward the
 * Vitest coverage denominator without any rendering.
 *
 * A `FilterFieldGroup` names one BUSINESS field of the story's field list, not one contract field:
 * "Innenmaße" is one group spanning six contract fields (three edges, each with a min and a max), and
 * counts as at most one deviation even when every edge carries a bound (see the plan's "Standing
 * constraints", "Der Badge zählt den angewendeten Stand").
 */

/**
 * The four field kinds the badge and the Sheet distinguish.
 */
export type FilterFieldKind = "statusSelection" | "text" | "range" | "triState";

/**
 * One business field of the story's field list. `fields` lists every contract field that participates —
 * a range or a set of dimension edges lists more than one; a status selection or a text field lists
 * exactly one. `columnId` names the table column the group's control renders in (Phase 2.1) — it is the
 * same id `activeFilterColumnIds` below derives the badge's column set from, and the same id
 * `renderColumnFilter`/`meta.filterColumnId` key off of in `DataTable`.
 */
export interface FilterFieldGroup<TFilter> {
    readonly kind: FilterFieldKind;
    readonly fields: ReadonlyArray<keyof TFilter>;
    readonly columnId: string;
}

/**
 * Blank strings normalize to `undefined` before comparison — an emptied text field must compare equal to
 * an untouched one, both meaning "no restriction". Arrays are left as arrays for `valuesEqual` to compare
 * as sets; every other value passes through unchanged.
 */
function normalize(value: unknown): unknown {
    if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed === "" ? undefined : trimmed;
    }
    return value;
}

/**
 * Structural equality used by `countDeviatingFilterGroups`. Arrays compare as sets (order never matters
 * for a status list); every other value compares via `===` after blank-string normalization.
 */
function valuesEqual(a: unknown, b: unknown): boolean {
    const left = normalize(a);
    const right = normalize(b);
    if (Array.isArray(left) && Array.isArray(right)) {
        if (left.length !== right.length) {
            return false;
        }
        const rightSet = new Set(right);
        return left.every((entry) => rightSet.has(entry));
    }
    return left === right;
}

/**
 * Whether a single group's applied value deviates from `defaults` — shared by `countDeviatingFilterGroups`
 * and `activeFilterColumnIds` below, both of which look at the same per-group question and differ only in
 * what they collect (a count vs. a set of columnIds).
 */
function groupDeviates<TFilter>(filters: TFilter, defaults: TFilter, group: FilterFieldGroup<TFilter>): boolean {
    return group.fields.some((field) => !valuesEqual(filters[field], defaults[field]));
}

/**
 * The badge count in the Restleiste (Phase 2.1, formerly the Sheet footer): the number of business field
 * groups whose applied value deviates from `defaults`, at most one per group regardless of how many
 * contract fields the group spans. `filters` is always the APPLIED state — filtering now takes effect
 * immediately (Standing constraint 4), there is no draft state left to describe. The free-text search and
 * the sort fields are not `TFilter` fields at all, so they never enter this count.
 */
export function countDeviatingFilterGroups<TFilter>(
    filters: TFilter,
    defaults: TFilter,
    groups: ReadonlyArray<FilterFieldGroup<TFilter>>
): number {
    let count = 0;
    for (const group of groups) {
        if (groupDeviates(filters, defaults, group)) {
            count += 1;
        }
    }
    return count;
}

/**
 * The set of table columns carrying an active filter (Phase 2.1) — the same per-group deviation check as
 * `countDeviatingFilterGroups`, collected as `columnId`s instead of counted. Two groups sharing a
 * `columnId` (the info column's print-image and blueprint groups) contribute the same entry, `Set`
 * collapses the duplicate.
 */
export function activeFilterColumnIds<TFilter>(
    filters: TFilter,
    defaults: TFilter,
    groups: ReadonlyArray<FilterFieldGroup<TFilter>>
): ReadonlySet<string> {
    const result = new Set<string>();
    for (const group of groups) {
        if (groupDeviates(filters, defaults, group)) {
            result.add(group.columnId);
        }
    }
    return result;
}
